import React, { useEffect } from 'react';
import { Star, ExternalLink } from 'lucide-react';

// Two HCF brand businesses on Trustpilot — link both for credibility.
const TRUSTPILOT_PROFILES = [
  { url: 'https://it.trustpilot.com/review/housecashflows.com', label: 'Housecashflows.com' },
  { url: 'https://it.trustpilot.com/review/hcf.estate', label: 'HCF.estate' },
];

// Mini Trustpilot social-proof strip + official widget loader.
// Once you provide the businessunit-id, drop it in TRUSTBOX_BU and the real widget renders.
// Until then, we link out clearly to Trustpilot for credibility.
const TRUSTBOX_BU = ''; // TODO: paste your businessunit-id from Trustpilot to enable the live widget

export default function TrustpilotSection() {
  // Inject the Trustpilot widget script (renders all .trustpilot-widget elements on the page)
  useEffect(() => {
    if (!TRUSTBOX_BU) return; // no widget while ID missing
    const id = 'trustpilot-bootstrap-script';
    if (document.getElementById(id)) {
      window.Trustpilot?.loadFromElement?.(document.querySelector('.trustpilot-widget'), true);
      return;
    }
    const s = document.createElement('script');
    s.id = id;
    s.async = true;
    s.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
    document.body.appendChild(s);
  }, []);

  return (
    <section className="px-4 md:px-12 py-10 md:py-14 bg-[var(--surface-2)] border-y border-border" data-testid="trustpilot-section">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
          {/* Left: rating */}
          <div className="text-center md:text-left">
            <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">Recensito su Trustpilot</div>
            <div className="flex items-center justify-center md:justify-start gap-1.5 mt-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-5 h-5 md:w-6 md:h-6 fill-[#00B67A] text-[#00B67A]" />
              ))}
              <span className="ml-2 font-serif text-xl md:text-2xl text-[hsl(var(--primary))]">Eccellente</span>
            </div>
            <p className="text-xs md:text-sm text-muted-foreground mt-2 max-w-md">
              I nostri clienti raccontano la loro esperienza con HCF Property su Trustpilot.
              Decine di recensioni reali, verificate e indipendenti.
            </p>
          </div>

          {/* Right: dual links */}
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            {TRUSTPILOT_PROFILES.map((tp) => (
              <a
                key={tp.url}
                href={tp.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-between gap-2 rounded-xl border border-border bg-white px-3 md:px-4 py-2.5 md:py-3 hover:border-[#00B67A] hover:shadow-md transition-all"
                data-testid={`trustpilot-link-${tp.label}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-md bg-[#00B67A] grid place-items-center shrink-0">
                    <Star className="w-3.5 h-3.5 fill-white text-white" />
                  </div>
                  <div className="text-left min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Trustpilot</div>
                    <div className="text-xs md:text-sm font-medium truncate">{tp.label}</div>
                  </div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground group-hover:text-[#00B67A] shrink-0" />
              </a>
            ))}
          </div>
        </div>

        {/* Optional live widget area — renders only when TRUSTBOX_BU is set */}
        {TRUSTBOX_BU && (
          <div className="mt-6">
            <div
              className="trustpilot-widget"
              data-locale="it-IT"
              data-template-id="53aa8912dec7e10d38f59f36"
              data-businessunit-id={TRUSTBOX_BU}
              data-style-height="140px"
              data-style-width="100%"
              data-theme="light"
              data-stars="4,5"
            >
              <a href={TRUSTPILOT_PROFILES[0].url} target="_blank" rel="noopener noreferrer">Trustpilot</a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
